import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  useRef,
} from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  makeStyles,
} from '@material-ui/core';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { useSnackbar } from 'notistack';

import { DialogoCliente2 } from '../../../Components';
// Não está no barrel de Components/Dialogos — mesmo caminho usado por
// DialogoCliente2 internamente.
import DialogoAutorizacao from '../../../Components/Dialogos/DialogoAutorizacao';
import { AutoCompleteRemoto } from '../../../Components/Inputs';
import { getOneAPI } from '../../../services';
import { formatCNPJ, formatCPF } from '../../../utils';
import {
  PRIMARY_COLOR,
  SECONDARY_COLOR,
  STATUS_ATIVO,
} from '../../../utils/constants';

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 16,
    padding: theme.spacing(1),
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(3),
  },
  iconContainer: {
    backgroundColor: 'rgba(0, 159, 214, 0.1)',
    borderRadius: '50%',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  icon: {
    fontSize: 40,
    color: '#009FD4',
  },
  title: {
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
  },
  content: {
    textAlign: 'center',
    padding: theme.spacing(2, 4),
  },
  opcoes: {
    marginTop: theme.spacing(2),
    alignItems: 'flex-start',
    width: '100%',
  },
  opcoesGrupo: {
    alignItems: 'flex-start',
  },
  linhaOpcao: {
    display: 'flex',
    alignItems: 'center',
  },
  liberado: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: '#2e7d32',
    fontSize: 13,
    fontWeight: 600,
  },
  clienteBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    textAlign: 'left',
    width: '100%',
  },
  clienteBoxAviso: {
    backgroundColor: '#fdecea',
    border: '1px solid #f5c2c7',
  },
  clienteBoxEscolha: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #90caf9',
  },
  camposBusca: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
    '& > *': {
      flex: 1,
    },
  },
  cartaoCliente: {
    display: 'flex',
    justifyContent: 'space-between',
    border: `1px solid ${PRIMARY_COLOR}`,
    borderRadius: 5,
    padding: 10,
    gap: 10,
    textAlign: 'left',
  },
  colunaCartao: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  linhaCartao: {
    display: 'flex',
    fontSize: 13,
    fontWeight: 'bold',
  },
  rotuloCartao: {
    color: SECONDARY_COLOR,
    marginRight: 6,
    marginBottom: 0,
    whiteSpace: 'nowrap',
  },
  valorCartao: {
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  actions: {
    justifyContent: 'center',
    padding: theme.spacing(2, 3, 3),
    gap: theme.spacing(2),
  },
  btnConfirmar: {
    borderRadius: 8,
    padding: '8px 30px',
    backgroundColor: '#009FD4',
    color: 'white',
    fontWeight: 'bold',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#007ca5',
    },
    '&:disabled': {
      backgroundColor: '#b0bec5',
      color: 'white',
    },
  },
  btnCancelar: {
    borderRadius: 8,
    padding: '8px 30px',
    fontWeight: 'bold',
    textTransform: 'none',
    color: '#757575',
  },
}));

const OPCAO_CONSUMO = 'consumo';
const OPCAO_REVENDA = 'revenda';
// Convenção do sistema inteiro (frente de caixa, pickers de cliente): venda
// sem cliente identificado usa o cliente-sentinela "Consumidor Final", id 1.
// vendas.cliente_id nunca é NULL.
const CLIENTE_ANONIMO_ID = 1;

// Só clientes ativos, com cadastro validado pela SEFAZ, e nunca o próprio
// "Consumidor Final" — mesmos filtros nos 4 campos de busca.
const filtrosBuscaCliente = {
  status: STATUS_ATIVO,
  ids_excluso: [CLIENTE_ANONIMO_ID],
  is_cadastro_completo_sefaz: true,
};

const DialogoConverterNfceParaNfe = forwardRef(
  ({ handleConfirma, handleRejeita = () => {} }, ref) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const refDialogoAutorizacao = useRef(null);
    const refDialogoCliente = useRef(null);
    // Busca de cliente (venda anônima) — mesmo esquema de 4 campos paralelos
    // (nome/fantasia/cpf/cnpj) + Enter passando o foco pra frente, igual ao
    // frente de caixa (DialogoInicioVenda) / OfertasJornal/DialogoSelecionarCliente.
    const refBuscaNome = useRef(null);
    const refBuscaNomeFantasia = useRef(null);
    const refBuscaCpf = useRef(null);
    const refBuscaCnpj = useRef(null);

    const [open, setOpen] = useState(false);
    const [codigo, setCodigo] = useState(null);
    const [finalidade, setFinalidade] = useState(OPCAO_REVENDA);

    const [cliente, setCliente] = useState(null);
    const [carregandoCliente, setCarregandoCliente] = useState(false);
    const [erroCliente, setErroCliente] = useState(false);
    const [clienteEscolhido, setClienteEscolhido] = useState(null);

    async function carregarCliente(clienteId) {
      if (!clienteId) {
        setCliente(null);
        return;
      }
      setCarregandoCliente(true);
      setErroCliente(false);
      try {
        const { data } = await getOneAPI('clientes', clienteId);
        setCliente(data);
      } catch (e) {
        setErroCliente(true);
        enqueueSnackbar(
          'Não foi possível carregar os dados do cliente desta venda.',
          { variant: 'error' },
        );
      } finally {
        setCarregandoCliente(false);
      }
    }

    useImperativeHandle(ref, () => ({
      // `clienteIdAtual` = vendas.cliente_id da linha clicada. O diálogo
      // decide sozinho, a partir dele, se precisa pedir escolha de cliente
      // (venda anônima) ou travar por cadastro incompleto perante a SEFAZ.
      handleOpen(codigoNew, clienteIdAtual) {
        setCodigo(codigoNew);
        setFinalidade(OPCAO_REVENDA);
        setClienteEscolhido(null);
        setCliente(null);
        setErroCliente(false);
        setOpen(true);
        carregarCliente(clienteIdAtual);
      },
    }));

    const handleClose = () => {
      setOpen(false);
      handleRejeita(codigo);
    };

    const anonima = !!cliente && cliente.id === CLIENTE_ANONIMO_ID;
    const cadastroIncompleto =
      !!cliente &&
      cliente.id !== CLIENTE_ANONIMO_ID &&
      !cliente.is_cadastro_completo_sefaz;
    const precisaEscolherCliente = anonima && !clienteEscolhido;

    const bloqueadoPorCliente =
      carregandoCliente ||
      erroCliente ||
      precisaEscolherCliente ||
      cadastroIncompleto;

    // A conversão emite dois documentos fiscais: só segue depois da digital de
    // um gerente (DialogoAutorizacao, mesma validação de sempre). O id do gerente
    // vai junto e o servidor grava quem converteu e quem autorizou.
    const handleSalvar = () => {
      if (bloqueadoPorCliente) return;
      if (refDialogoAutorizacao.current) {
        refDialogoAutorizacao.current.handleOpen();
      }
    };

    const confirmarComAutorizador = (autorizadorId) => {
      setOpen(false);
      handleConfirma(codigo, {
        revenda: finalidade === OPCAO_REVENDA,
        ...(clienteEscolhido && { cliente_id: clienteEscolhido.id }),
        autorizador_id: autorizadorId,
      });
    };

    return (
      <>
        <Dialog
          open={open}
          onClose={handleClose}
          classes={{ paper: classes.paper }}
          disableBackdropClick
          disableEscapeKeyDown
          maxWidth={anonima ? 'sm' : 'xs'}
          fullWidth
        >
          <Box className={classes.header}>
            <div className={classes.iconContainer}>
              <SwapHorizIcon className={classes.icon} />
            </div>
            <Typography variant="h6" className={classes.title}>
              Converter NFC-e em NF-e
            </Typography>
          </Box>

          <DialogContent className={classes.content}>
            <DialogContentText style={{ color: '#666' }}>
              Será emitida uma NF-e de devolução da NFC-e e, em seguida, uma
              NF-e de venda. A NFC-e original ficará registrada como devolvida.
              Esta ação não pode ser desfeita e exige a digital de um gerente.
              Deseja continuar?
            </DialogContentText>

            <FormControl component="fieldset" className={classes.opcoes}>
              <FormLabel component="legend">Finalidade da NF-e</FormLabel>
              <RadioGroup
                className={classes.opcoesGrupo}
                value={finalidade}
                onChange={(e) => setFinalidade(e.target.value)}
              >
                <FormControlLabel
                  value={OPCAO_REVENDA}
                  control={<Radio color="primary" />}
                  label="Revenda"
                />
                <FormControlLabel
                  value={OPCAO_CONSUMO}
                  control={<Radio color="primary" />}
                  label="Uso e consumo"
                />
              </RadioGroup>
            </FormControl>

            {carregandoCliente && (
              <Box marginTop={2}>
                <CircularProgress size={22} />
              </Box>
            )}

            {anonima && (
              <Box
                className={`${classes.clienteBox} ${classes.clienteBoxEscolha}`}
              >
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  Esta venda não tem cliente identificado. Busque por nome, CPF
                  ou CNPJ um cliente com cadastro validado pela SEFAZ:
                </Typography>

                <Box className={classes.camposBusca}>
                  <AutoCompleteRemoto
                    ref={refBuscaNome}
                    id="clienteConversao"
                    name="clienteConversao"
                    label="Nome"
                    campo="nome"
                    resource="clientes"
                    nested={['cidade.estado']}
                    toString={false}
                    value={clienteEscolhido}
                    onChange={(valor) => setClienteEscolhido(valor)}
                    handleEnter={() => {
                      refBuscaNomeFantasia.current?.focus();
                      refBuscaNomeFantasia.current?.select();
                    }}
                    fullwidth
                    autoFocus
                    filters={filtrosBuscaCliente}
                  />
                  <AutoCompleteRemoto
                    ref={refBuscaNomeFantasia}
                    id="clienteConversao"
                    name="clienteConversao"
                    label="Nome Fantasia"
                    campo="nome_fantasia"
                    resource="clientes"
                    nested={['cidade.estado']}
                    toString={false}
                    value={clienteEscolhido}
                    onChange={(valor) => setClienteEscolhido(valor)}
                    handleEnter={() => {
                      refBuscaCpf.current?.focus();
                      refBuscaCpf.current?.select();
                    }}
                    fullwidth
                    filters={filtrosBuscaCliente}
                  />
                </Box>
                <Box className={classes.camposBusca}>
                  <AutoCompleteRemoto
                    ref={refBuscaCpf}
                    id="clienteConversao"
                    name="clienteConversao"
                    label="CPF"
                    campo="cpf"
                    campoPesquisa="cpf_search"
                    resource="clientes"
                    nested={['cidade.estado']}
                    toString={false}
                    value={clienteEscolhido}
                    onChange={(valor) => setClienteEscolhido(valor)}
                    handleEnter={() => {
                      refBuscaCnpj.current?.focus();
                      refBuscaCnpj.current?.select();
                    }}
                    fullwidth
                    filters={filtrosBuscaCliente}
                  />
                  <AutoCompleteRemoto
                    ref={refBuscaCnpj}
                    id="clienteConversao"
                    name="clienteConversao"
                    label="CNPJ"
                    campo="cnpj"
                    campoPesquisa="cnpj_search"
                    resource="clientes"
                    nested={['cidade.estado']}
                    toString={false}
                    value={clienteEscolhido}
                    onChange={(valor) => setClienteEscolhido(valor)}
                    handleEnter={() => {
                      refBuscaNome.current?.focus();
                      refBuscaNome.current?.select();
                    }}
                    fullwidth
                    filters={filtrosBuscaCliente}
                  />
                </Box>

                {clienteEscolhido && (
                  <Box className={classes.cartaoCliente}>
                    <Box className={classes.colunaCartao}>
                      <Box className={classes.linhaCartao}>
                        <p className={classes.rotuloCartao}>Responsável:</p>
                        <p className={classes.valorCartao}>
                          {clienteEscolhido.nome}
                        </p>
                      </Box>
                      <Box className={classes.linhaCartao}>
                        <p className={classes.rotuloCartao}>Razão Social:</p>
                        <p className={classes.valorCartao}>
                          {clienteEscolhido.razao_social}
                        </p>
                      </Box>
                      <Box className={classes.linhaCartao}>
                        <p className={classes.rotuloCartao}>CPF:</p>
                        <p className={classes.valorCartao}>
                          {formatCPF(clienteEscolhido.cpf)}
                        </p>
                      </Box>
                      <Box className={classes.linhaCartao}>
                        <p className={classes.rotuloCartao}>CNPJ:</p>
                        <p className={classes.valorCartao}>
                          {formatCNPJ(clienteEscolhido.cnpj)}
                        </p>
                      </Box>
                    </Box>
                    <Box className={classes.colunaCartao}>
                      <Box className={classes.linhaCartao}>
                        <p className={classes.rotuloCartao}>Fantasia:</p>
                        <p className={classes.valorCartao}>
                          {clienteEscolhido.nome_fantasia}
                        </p>
                      </Box>
                      <Box className={classes.linhaCartao}>
                        <p className={classes.rotuloCartao}>Bairro:</p>
                        <p className={classes.valorCartao}>
                          {clienteEscolhido.bairro}
                        </p>
                      </Box>
                      <Box className={classes.linhaCartao}>
                        <p className={classes.rotuloCartao}>Cidade:</p>
                        <p className={classes.valorCartao}>
                          {clienteEscolhido.cidade
                            ? `${clienteEscolhido.cidade.nome} - ${clienteEscolhido.cidade.estado.sigla}`
                            : ''}
                        </p>
                      </Box>
                      <Box className={classes.linhaCartao}>
                        <p className={classes.rotuloCartao}>Logradouro:</p>
                        <p className={classes.valorCartao}>
                          {clienteEscolhido.logradouro}
                        </p>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {cadastroIncompleto && (
              <Box
                className={`${classes.clienteBox} ${classes.clienteBoxAviso}`}
              >
                <Typography
                  variant="body2"
                  style={{ color: '#721c24', marginBottom: 8 }}
                >
                  O cadastro de <strong>{cliente.nome}</strong> está incompleto
                  perante a SEFAZ. Ajuste o cadastro antes de converter.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    if (refDialogoCliente.current) {
                      refDialogoCliente.current.handleEdit(cliente.id);
                    }
                  }}
                  tabIndex={-1}
                >
                  Editar cadastro do cliente
                </Button>
              </Box>
            )}
          </DialogContent>

          <DialogActions className={classes.actions}>
            <Button
              onClick={handleClose}
              className={classes.btnCancelar}
              tabIndex={-1}
            >
              Não
            </Button>
            <Button
              onClick={handleSalvar}
              variant="contained"
              className={classes.btnConfirmar}
              disabled={bloqueadoPorCliente}
              autoFocus
              tabIndex={-1}
            >
              Sim
            </Button>
          </DialogActions>
        </Dialog>

        <DialogoAutorizacao
          ref={refDialogoAutorizacao}
          usar_biometria
          title="Autorizar conversão de NFC-e em NF-e"
          enqueueSnackbar={enqueueSnackbar}
          mensagemSucesso="Conversão autorizada."
          handleSalvar={confirmarComAutorizador}
        />

        <DialogoCliente2
          ref={refDialogoCliente}
          handleClose={(idAtualizado) => {
            if (idAtualizado > 0) carregarCliente(idAtualizado);
          }}
        />
      </>
    );
  },
);

export default DialogoConverterNfceParaNfe;
